/**
 * An animated GLB bird, standing in for the one that was baked into the splat
 * capture. A scene gets one by declaring `crowProp` in its `SceneConfig`; the
 * `?crow` tuner panel only exists to place and time it.
 *
 * Model: "Crow" by Alexei Ostapenko (https://sketchfab.com/alexanders823),
 * https://sketchfab.com/3d-models/crow-d5a9b0df4da3493688b63ce42c8a83e2,
 * CC BY 4.0. Used with the file unmodified but `metalness` forced to 0 at
 * runtime and only sub-ranges of the source clip played. CC BY wants the credit
 * where the audience can see it — there is no credits surface in the app yet,
 * so right now this comment is the only place it exists.
 *
 * Still thin for a production subsystem: no quality-tier handling, and the model
 * is a single LOD loaded on every device.
 *
 * ponytail: while a clip plays, `needsRerender()` is true every frame, so the
 * idle-render scheduler cannot park and the scene renders at full rate (see
 * `.claude/rules/perf-budget.md`). That is why this sits behind a URL flag, and
 * why `pause()` exists — it lets the scene reach `paused` again. A production
 * bird must be a one-shot take-off that ENDS, not a permanent loop.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

import { disposeSceneGraph } from '../utils/disposeSceneResources';
import { loadWithCdnFallback } from '../../utils/loadWithCdnFallback';
import {
  FLAP_WINDOW,
  FOLD_WINDOW,
  bearingForFacing,
  sampleCrowFlight,
  type CrowFlightParams,
} from './crowFlight';

/** Crossfade from flapping into the wing-fold as the bird touches down. */
const LANDING_FADE_SECONDS = 0.25;

/**
 * Sketchfab exports this crow as fully metallic (`metallicFactor: 1`) because its
 * own viewer always supplies an environment map. Our splat scenes never set
 * `scene.environment` — `EnvironmentManager` only ever nulls it — and in PBR a
 * metal has NO diffuse term, so with nothing to reflect the bird renders as a
 * black silhouette no matter how many lights the scene has. Treating it as a
 * dielectric is what lets the hemisphere and directional lights reach it.
 */
export const DEFAULT_METALNESS = 0;

/** Feathers: a little sheen, nowhere near a mirror. */
export const DEFAULT_ROUGHNESS = 0.65;

export interface CrowPropOptions {
  scene: THREE.Scene;
  url: string;
  /** Where to drop the bird on load — usually the scene's orbit center. */
  initialPosition: THREE.Vector3;
  /**
   * Longest-axis size in SCENE units, not a raw scale factor. Source models
   * carry wildly different units — this crow is 103.6 long at scale 1 (Sketchfab
   * exports in centimetres) — so a raw scale is meaningless until you have
   * measured the file. Normalising here keeps the tuner honest and survives
   * swapping the GLB for a different bird.
   */
  initialLength: number;
  /** For the `Asset CDN Fallback` analytics event. */
  sceneId?: string | null;
}

export class CrowProp {
  private scene: THREE.Scene | null;
  private root: THREE.Object3D | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private clips: THREE.AnimationClip[] = [];
  private playing = false;
  private disposed = false;
  /** Longest axis of the source model at scale 1. Set once, on load. */
  private baseLength = 1;

  // --- Flight ---
  private flapAction: THREE.AnimationAction | null = null;
  private foldAction: THREE.AnimationAction | null = null;
  private flapClip: THREE.AnimationClip | null = null;
  private foldClip: THREE.AnimationClip | null = null;
  private flapWindow = { ...FLAP_WINDOW };
  private flight: CrowFlightParams | null = null;
  private flightElapsed = 0;
  private landingStarted = false;

  /** Last clip handed to `play()` — `replay()` needs it, the tuner shows it. */
  public activeClipName: string | null = null;
  public loadPromise: Promise<void>;

  constructor({ scene, url, initialPosition, initialLength, sceneId }: CrowPropOptions) {
    this.scene = scene;
    // Every other asset in the app loads through this: try the CDN, fall back to
    // the local origin. Loading straight through `GLTFLoader` skips the fallback
    // entirely, so an asset that is not on the CDN yet simply 404s for anyone
    // whose `.env` sets `VITE_CDN_BASE_URL`, even though the file is right there
    // in `public/`.
    const loader = new GLTFLoader();
    this.loadPromise = loadWithCdnFallback(
      url,
      (resolvedUrl) => loader.loadAsync(resolvedUrl),
      { assetType: 'glb', sceneId },
    ).then((gltf) => {
      // Loaders have no abort (webgl-dispose rule): if teardown already ran,
      // dispose what just arrived instead of attaching it to a dead scene.
      if (this.disposed) {
        disposeSceneGraph(gltf.scene);
        return;
      }
      const root = gltf.scene;
      // Measure at scale 1, in the bind pose, before anything is applied.
      const size = new THREE.Box3().setFromObject(root).getSize(new THREE.Vector3());
      this.baseLength = Math.max(size.x, size.y, size.z) || 1;

      root.position.copy(initialPosition);
      this.scene?.add(root);
      this.root = root;
      this.setLength(initialLength);
      this.setMetalness(DEFAULT_METALNESS);
      this.setRoughness(DEFAULT_ROUGHNESS);
      this.clips = gltf.animations;

      if (this.clips.length > 0) {
        this.mixer = new THREE.AnimationMixer(root);
        // A one-shot clip that ends must also stop marking frames dirty —
        // otherwise the scene keeps rendering at full rate over a frozen bird.
        this.mixer.addEventListener('finished', () => { this.playing = false; });
        // Load PAUSED — the source clip is a placeholder until our own motion
        // exists. `update(0)` settles the skeleton on the clip's first frame
        // (perched, wings folded) rather than leaving it in the raw bind pose,
        // which for a rigged bird is a spread-eagle. Turn `playing` on in the
        // tuner, or hit Replay, to see the original take-off.
        this.play(this.clips[0].name);
        this.mixer.update(0);
        this.playing = false;
        this.buildFlightActions();
      }
      console.warn(
        '[CrowProp] loaded',
        JSON.stringify(
          {
            clips: this.clips.map((c) => c.name),
            sourceLongestAxis: +this.baseLength.toFixed(2),
            lengthInSceneUnits: initialLength,
            position: root.position.toArray().map((v) => +v.toFixed(3)),
          },
          null,
          2,
        ),
      );
    });
  }

  update(delta: number): void {
    if (this.flight) {
      this.advanceFlight(delta);
    }
    if (this.playing) {
      this.mixer?.update(delta);
    }
  }

  /** Feeds `isFrameDirty`. False once landed, so the scene can park. */
  needsRerender(): boolean {
    return !this.disposed && (this.playing || this.flight !== null);
  }

  get isFlying(): boolean {
    return this.flight !== null;
  }

  /**
   * Take off, fly one circle, land back on `params.origin`. The source clip has
   * no root motion, so the path here is the only thing that moves the bird —
   * the clip only supplies wingbeats.
   */
  startFlight(params: CrowFlightParams): void {
    const flap = this.flapAction;
    const root = this.root;
    if (!flap || !root || !this.mixer) return;

    // Restarting mid-air must NOT adopt the airborne position as the new perch,
    // or the loop closes around a point in the sky and the bird ends up hanging
    // there. Keep the perch the flight started from.
    const origin = this.flight ? this.flight.origin : params.origin;

    // The bird leaves along the direction its head already points: the loop's
    // bearing is derived from the current facing, not the other way round. So
    // `rot Y` and the gizmo ARE the flight-direction control, and there is
    // nothing to turn through at take-off.
    const aimed = { ...params, origin };
    this.flight = { ...aimed, bearing: bearingForFacing(aimed, root.rotation.y) };
    this.flightElapsed = 0;
    this.landingStarted = false;

    // Drop the perched pose action too — left running it blends against the
    // wingbeat at equal weight and half-mutes it.
    this.mixer.stopAllAction();
    flap.reset();
    flap.setLoop(THREE.LoopRepeat, Infinity);
    flap.setEffectiveWeight(1);
    flap.play();
    this.playing = true;
  }

  /** Abort mid-air and put the bird back on its perch, wings folded. */
  stopFlight(): void {
    const origin = this.flight?.origin;
    this.flight = null;
    this.landingStarted = false;
    this.flapAction?.stop();
    this.foldAction?.stop();
    if (origin) this.setPosition(origin.x, origin.y, origin.z);
    this.root?.rotation.set(0, this.root.rotation.y, 0);
    this.perch();
  }

  /** Seconds within the source clip used as the looping wingbeat. */
  setFlapWindow(start: number, end: number): void {
    if (end - start < 0.05) return;
    this.flapWindow = { start, end };
    this.buildFlightActions();
  }

  private advanceFlight(delta: number): void {
    const flight = this.flight;
    const root = this.root;
    if (!flight || !root) return;

    this.flightElapsed += delta;
    const progress = Math.min(1, this.flightElapsed / flight.duration);
    const sample = sampleCrowFlight(flight, progress);
    root.position.set(sample.x, sample.y, sample.z);
    // YXZ: yaw first, then roll about the already-turned local axis. Which axis
    // reads as "roll" depends on the model's forward — set `bank` to 0 if this
    // crow banks the wrong way.
    root.rotation.set(0, sample.heading, sample.bank, 'YXZ');

    // Fold the wings while the bird is still settling, not after it has landed.
    if (!this.landingStarted && this.flightElapsed >= flight.duration - LANDING_FADE_SECONDS) {
      this.landingStarted = true;
      this.startFold();
    }

    if (progress >= 1) {
      // `playing` stays true until the fold action finishes — the mixer's
      // 'finished' listener clears it, and only then can the scene park.
      this.flight = null;
    }
  }

  private startFold(): void {
    const flap = this.flapAction;
    const fold = this.foldAction;
    if (!flap || !fold) return;
    fold.reset();
    fold.setLoop(THREE.LoopOnce, 1);
    fold.clampWhenFinished = true;
    fold.play();
    flap.crossFadeTo(fold, LANDING_FADE_SECONDS, false);
  }

  /** Settle on the source clip's first frame — perched, wings folded. */
  private perch(): void {
    if (!this.mixer || this.clips.length === 0) return;
    this.play(this.clips[0].name);
    this.mixer.update(0);
    this.playing = false;
  }

  private buildFlightActions(): void {
    const mixer = this.mixer;
    const source = this.clips[0];
    if (!mixer || !source) return;

    // Rebuilt whenever the flap window changes; drop the previous clips or the
    // mixer accumulates a cached action per edit.
    if (this.flapClip) mixer.uncacheClip(this.flapClip);
    if (this.foldClip) mixer.uncacheClip(this.foldClip);

    // `subclip` measures in FRAMES — passing fps = 1 makes the window seconds,
    // which is how the clip was measured in the first place.
    this.flapClip = THREE.AnimationUtils.subclip(
      source, 'crow-flap', this.flapWindow.start, this.flapWindow.end, 1,
    );
    this.foldClip = THREE.AnimationUtils.subclip(
      source, 'crow-fold', FOLD_WINDOW.start, FOLD_WINDOW.end, 1,
    );
    this.flapAction = mixer.clipAction(this.flapClip);
    this.foldAction = mixer.clipAction(this.foldClip);
  }

  get clipNames(): string[] {
    return this.clips.map((clip) => clip.name);
  }

  get object(): THREE.Object3D | null {
    return this.root;
  }

  /**
   * Move every node onto one render layer. `createScene` puts the bird on the
   * overlay layer so it can keep animating over a frozen frame while the splats
   * are not redrawn at all.
   */
  setRenderLayer(layer: number): void {
    this.root?.traverse((node) => node.layers.set(layer));
  }

  /**
   * `loop: false` is what a production take-off would do — play once, hold the
   * last pose, and let the render scheduler park again.
   */
  play(clipName: string, loop = true): void {
    const clip = this.clips.find((c) => c.name === clipName);
    if (!this.mixer || !clip) return;
    this.mixer.stopAllAction();
    const action = this.mixer.clipAction(clip);
    action.reset();
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = !loop;
    action.play();
    this.activeClipName = clipName;
    this.playing = true;
  }

  /** Restart the current clip — the "watch the take-off again" button. */
  replay(loop: boolean): void {
    if (this.activeClipName) this.play(this.activeClipName, loop);
  }

  setPlaying(playing: boolean): void {
    this.playing = playing;
  }

  setTimeScale(timeScale: number): void {
    if (this.mixer) this.mixer.timeScale = timeScale;
  }

  /** `length` is the longest axis in scene units — see `initialLength`. */
  setLength(length: number): void {
    this.root?.scale.setScalar(length / this.baseLength);
  }

  /** Current longest-axis size, in scene units. */
  get length(): number {
    return (this.root?.scale.x ?? 0) * this.baseLength;
  }

  setPosition(x: number, y: number, z: number): void {
    this.root?.position.set(x, y, z);
  }

  // --- Externally driven motion ---
  //
  // The scene-opening tour owns its own timing and the camera, so it drives the
  // bird frame by frame instead of handing over a path. These three are all it
  // needs: start the wings, write a pose, fold on touchdown. Wing clips and the
  // transform stay this class's business either way.

  /** Loop the wingbeat. `update` keeps ticking it while `playing` is true. */
  startWings(): void {
    const flap = this.flapAction;
    if (!flap || !this.mixer) return;
    this.foldAction?.stop();
    this.mixer.stopAllAction();
    flap.reset();
    flap.setLoop(THREE.LoopRepeat, Infinity);
    flap.setEffectiveWeight(1);
    flap.play();
    this.playing = true;
  }

  /** Fold the wings back down — the same crossfade a flight lands with. */
  foldWings(): void {
    this.startFold();
  }

  applyPose(x: number, y: number, z: number, heading: number, bank: number): void {
    // YXZ so the roll is applied about the already-turned axis, matching how
    // `advanceFlight` writes its own poses. The GLB faces +Z with its left wing
    // on +X, so a positive `bank` lifts the LEFT wing: a right-hand turn.
    this.root?.position.set(x, y, z);
    this.root?.rotation.set(0, heading, bank, 'YXZ');
  }

  /** See `DEFAULT_METALNESS` — at 1 the bird is a black silhouette. */
  setMetalness(metalness: number): void {
    this.forEachStandardMaterial((material) => { material.metalness = metalness; });
  }

  setRoughness(roughness: number): void {
    this.forEachStandardMaterial((material) => { material.roughness = roughness; });
  }

  private forEachStandardMaterial(apply: (material: THREE.MeshStandardMaterial) => void): void {
    this.root?.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const candidate of materials) {
        const material = candidate as THREE.MeshStandardMaterial;
        if (material.isMeshStandardMaterial) apply(material);
      }
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.playing = false;
    this.flight = null;
    this.mixer?.stopAllAction();
    this.mixer = null;
    this.clips = [];
    this.flapAction = null;
    this.foldAction = null;
    this.flapClip = null;
    this.foldClip = null;
    if (this.root) {
      // disposeSceneGraph BEFORE detaching — traverse does not reach a
      // detached subtree's children (webgl-dispose rule).
      disposeSceneGraph(this.root);
      this.root.removeFromParent();
      this.root = null;
    }
    this.scene = null;
  }
}
